import React from 'react';
import classNames from 'classnames';

import styles from './styles.module.scss';
import { useCallAudioPlayer } from './hooks';
import Transcript from './components/transcript';
import Controls from './components/controls';
import Waveform from './components/waveform';
import Time from './components/time';

import type { TranscriptItem } from 'infrastructure/hooks/calls/use-get-call-transcribe/types';

interface ITimeComponentProps {
  currentTimeSeconds: number;
  durationSeconds: number;
  callId: string;
}

interface Props {
  audioUrl: string;
  callId: string;
  transcripts?: TranscriptItem[] | null;
  transcriptsLoading: boolean;
  showTranscript: boolean;
  toggleShowTranscript: () => void;
  callTranscribeErrorMessage?: string;
  playerContainerClassName?: string;
  timeComponent?: (prps: ITimeComponentProps) => React.ReactNode;
}

const CallAudioPlayer: React.FC<Props> = ({
  audioUrl,
  callId,
  transcripts,
  transcriptsLoading,
  showTranscript,
  toggleShowTranscript,
  callTranscribeErrorMessage,
  playerContainerClassName,
  timeComponent,
}) => {
  const {
    waveformRef,
    isPlaying,
    volume,
    isMuted,
    currentTimeSeconds,
    durationSeconds,
    isLoading,
    togglePlayPause,
    stop,
    rewind,
    forward,
    toggleMute,
    handleVolumeChange,
    setCurrentTimeSeconds,
  } = useCallAudioPlayer(audioUrl);

  const TimeBlock = timeComponent ? (
    timeComponent({
      currentTimeSeconds,
      durationSeconds,
      callId,
    })
  ) : (
    <Time
      callId={callId}
      currentTimeSeconds={currentTimeSeconds}
      durationSeconds={durationSeconds}
    />
  );

  return (
    <div>
      <div
        className={classNames(
          styles['player-container'],
          playerContainerClassName,
        )}
      >
        <Waveform isLoading={isLoading} waveformRef={waveformRef} />
        {TimeBlock}
        <Controls
          isLoading={isLoading}
          rewind={rewind}
          togglePlayPause={togglePlayPause}
          forward={forward}
          stop={stop}
          isPlaying={isPlaying}
          volume={volume}
          toggleMute={toggleMute}
          isMuted={isMuted}
          handleVolumeChange={handleVolumeChange}
          toggleShowTranscript={toggleShowTranscript}
          showTranscript={showTranscript}
        />
      </div>
      {showTranscript && (
        <Transcript
          transcripts={transcripts}
          transcriptsLoading={transcriptsLoading}
          callTranscribeErrorMessage={callTranscribeErrorMessage}
          currentTimeSeconds={currentTimeSeconds}
          setCurrentTimeSeconds={setCurrentTimeSeconds}
        />
      )}
    </div>
  );
};

export default CallAudioPlayer;
